import React, { FC, useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
} from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import SaveButton from "../../../components/SaveButton";
import SettingSection from "../../../components/SettingSection";
import { DialogController } from "../../../hooks/use-dialog";
import {
  useListUsersInOrganizationQuery,
  useUpdateOrganizationMutation,
} from "../../../store/pomeriumApi";

export type ChangeOwnerParams = {
  name: string;
  orgId: string;
  ownerId: string;
};

type Props = {
  dialog: DialogController<ChangeOwnerParams>;
};

const ChangeOwner: FC<Props> = ({ dialog }) => {
  const [owner, setOwner] = useState<string>("");
  const [updateOrg] = useUpdateOrganizationMutation();

  const { data: users } = useListUsersInOrganizationQuery(
    {
      organizationId: dialog.data?.orgId || "",
      userType: "user_type_interactive",
    },
    { skip: !dialog.data?.orgId },
  );

  const usersToChangeTo = users?.filter(
    (u) => u.id !== dialog?.data?.ownerId && u.organizationRole === "admin",
  );

  useEffect(() => {
    if (!dialog.open) {
      setOwner("");
    }
  }, [dialog.open]);
  const handleSave = async () => {
    const update = await updateOrg({
      organizationId: dialog?.data?.orgId || "",
      updateOrganizationRequest: {
        name: dialog?.data?.name || "",
        ownerUserId: owner,
      },
    }).unwrap();
    if (update?.id) {
      dialog.handleClose();
    }
  };

  if (!dialog?.data?.orgId || typeof users === "undefined") {
    return <></>;
  }

  return (
    <Dialog
      open={dialog.open}
      onClose={dialog.handleClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
        Change Owner of {dialog?.data?.name}
        <IconButton
          color="primary"
          onClick={() => dialog.handleClose()}
          size="large"
          sx={{ ml: "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Container disableGutters>
          {!usersToChangeTo?.length && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No other administrators exist for this organization. Please invite
              an administrator to change ownership.
            </Alert>
          )}
          {!!usersToChangeTo?.length && (
            <Stack>
              <SettingSection label="Owner" docsKey="">
                <MenuList dense sx={{ p: 0 }}>
                  {users
                    ?.filter(
                      (u) =>
                        u.id !== dialog?.data?.ownerId &&
                        u.organizationRole === "admin",
                    )
                    .map((user) => {
                      const isSelected = user.id === owner;

                      return (
                        <MenuItem
                          key={"owner" + user.id}
                          onClick={() => setOwner(user.id)}
                          selected={isSelected}
                          sx={{ borderRadius: 1 }}
                        >
                          <ListItemText>
                            {user?.displayName || user?.email}
                          </ListItemText>
                        </MenuItem>
                      );
                    })}
                </MenuList>
              </SettingSection>
            </Stack>
          )}
        </Container>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => dialog.handleClose()}>Cancel</Button>
        <SaveButton
          color="primary"
          onClick={handleSave}
          disabled={!owner || !usersToChangeTo?.length}
        >
          Save
        </SaveButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeOwner;
